import { Sub, TStyles, TSubProperty } from "@superwall-me/schema"
import { defaultDivComponent } from "app/paywallBuilderV2/components/ElementDetails/utils"
import {
  createCompactFooter,
  createComponent,
  createDefaultFooter,
  createMinimalFooter,
} from "app/paywallBuilderV2/contexts/JsonProvider/utils"
import { createComponentId } from "app/paywallBuilderV2/helpers"
import { TElement } from "app/schema/paywallTemplate/element/element"
import { TPageSettings } from "app/schema/paywallTemplate/pageSettings"
import _ from "lodash"

// Drawer Ids ------------------------------
const drawerId = "__sw-0E8JE29eCMpfI86XEzTrw"
const div1Id = "__sw-7S3KE84fVLroQ44XEaWhn"
const div2Id = "__sw-0E8JE29eCMpfI30DEzBoi"
const productsId = "__sw-FxoDJNtKhOQjxMh7INqF5"

// Footer Ids ------------------------------
const footerId = "__sw-5G9WT12cJAbkL30VFuHDq"
const footerItem1Id = "__sw-0H7LR61cYBzwG78KCiMso"
const footerItem2Id = "__sw-3K9SE29eCMpfI86XEzIsp"
const footerItem3Id = "__sw-CciGJVtJd9EkxMr7IFeT1"
const footerItem4Id = "__sw-LroCOEtVyOEjxP93VNw0K"
const footerItem5Id = "__sw-8JwCiWIf9E2j8JdwOOc07"
const footerItem6Id = "__sw-S03KnwkUnE8kj08rKKd01"

export const footerIds = [
  footerId,
  footerItem1Id,
  footerItem2Id,
  footerItem3Id,
  footerItem4Id,
  footerItem5Id,
  footerItem6Id,
]

export const footerDividerId = "__sw-4J4NQ29eCMpfL23XEzYo9"

/**
 * Takes two style arrays from two different style props and merges them together.
 * Any duplicate properties names will be overwritten by the second array.
 */
const mergeAndDedupeStyles = (a: TStyles = [], b: TStyles = []): TStyles => {
  let objMap = {}

  a.forEach(({ property, value }) => {
    objMap[property] = value
  })

  b.forEach(({ property, value }) => {
    objMap[property] = value
  })

  return Object.entries(objMap).map(([k, v]) => ({ property: k, value: v as string }))
}

// https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
// ?
export const stringToHash = (seed: string): number => {
  var hash = 0

  if (seed.length == 0) return hash * hash

  for (let i = 0; i < seed.length; i++) {
    let char = seed.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }

  return hash * hash
}

// This will check inside the customCSS string and replace any id selectors that are using
// the `swElement{id}` id generated in pjs, specifically for product selectors. Since the
// key for those products is changing with this migration, the generated hash at runtime is
// also different so we need to make sure the new hash is used in the customCSS string.
export const productsSwElementHasMigrationScript = (customCss: string): string => {
  const legacyProduct1SubKeyHash = stringToHash("Purchase Drawer > Product")
  const legacyProduct2SubKeyHash = stringToHash("Purchase Drawer > Product 2")
  const legacyProduct3SubKeyHash = stringToHash("Purchase Drawer > Product 3")
  const newProduct1SubKeyHash = stringToHash(`Products ${productsId.toLowerCase()} > Product`)
  const newProduct2SubKeyHash = stringToHash(`Products ${productsId.toLowerCase()} > Product 2`)
  const newProduct3SubKeyHash = stringToHash(`Products ${productsId.toLowerCase()} > Product 3`)

  let css = customCss

  if (css.includes(`${legacyProduct1SubKeyHash}`)) {
    css = css.replace(`${legacyProduct1SubKeyHash}`, `${newProduct1SubKeyHash}`)
  }
  if (css.includes(`${legacyProduct2SubKeyHash}`)) {
    css = css.replace(`${legacyProduct2SubKeyHash}`, `${newProduct2SubKeyHash}`)
  }
  if (css.includes(`${legacyProduct3SubKeyHash}`)) {
    css = css.replace(`${legacyProduct3SubKeyHash}`, `${newProduct3SubKeyHash}`)
  }
  return css
}

export const visibilityNoneToDisplayNoneMigrationScript = (substitutions: Sub[]): Sub[] => {
  return substitutions.map((sub) => {
    const [stylesProps, restPropsA] = _.partition(
      sub?.properties,
      (prop) => prop?.property?.type === "styles" && prop.prefix === "default"
    )
    const [visibilityProp, restPropsB] = _.partition(
      restPropsA,
      (prop) => prop?.property?.type === "hidden" && prop?.property?.hidden === true
    )
    if (!visibilityProp.length) return sub

    return {
      ...sub,
      properties: [
        ...restPropsB,
        {
          prefix: "default",
          property: {
            type: "styles",
            styles: [
              // @ts-ignore
              ...(stylesProps[0]?.property?.styles || []),
              { property: "display", value: "none" },
            ],
          },
        },
      ],
    }
  })
}

export const productDrawerSubstitutionMigrationScript = (
  substitutions: Sub[],
  pageSettings: TPageSettings
): Sub[] => {
  const [purchaseDrawerSubs, restSubs] = _.partition(substitutions, (sub) =>
    sub.key.startsWith("Purchase Drawer")
  )

  let newDrawerAndProductsSubs = purchaseDrawerSubs.map((sub) => {
    if (sub.key === "Purchase Drawer") {
      const baseSub = {
        ...sub,
        key: `Drawer ${drawerId.toLowerCase()}`,
      }

      /**
       * Whenever the footer position is set to "inline" we want to make sure we remove
       * extra spacing at the bottom of the drawer.
       */
      if (pageSettings?.footerPosition === "inline") {
        const [stylesProps, restProps] = _.partition(sub.properties, (prop) => {
          return prop.property.type === "styles" && prop.prefix === "default"
        })
        return {
          ...baseSub,
          properties: [
            ...(restProps || []),
            {
              prefix: "default",
              property: {
                type: "styles",
                styles: [
                  // @ts-ignore
                  ...(stylesProps[0]?.property?.styles || []),
                  { property: "padding-bottom", value: "calc(var(--sw-container-padding) + 10px)" },
                ],
              },
            },
          ],
        }
      }

      return baseSub
    } else if (sub.key === "Purchase Drawer > Card Content Container") {
      return {
        ...sub,
        key: `Div ${div1Id.toLowerCase()}`,
      }
    } else if (
      sub.key === "Purchase Drawer > Container" ||
      sub.key === "Purchase Drawer > Has Content"
    ) {
      return {
        ...sub,
        key: `Div ${div2Id.toLowerCase()}`,
      }
    } else if (sub.key === "Purchase Drawer > Drawer Inner Container") {
      const baseSub = {
        ...sub,
        key: `Products ${productsId.toLowerCase()} > Products Section Inner Container`,
      }

      if (pageSettings?.footerPosition === "fixed") {
        const [stylesProps, restProps] = _.partition(sub.properties, (prop) => {
          return prop.property.type === "styles" && prop.prefix === "default"
        })
        const purchaseButtonContainer = substitutions.find(
          (sub) => sub.key === "Purchase Drawer > Purchase Button Container"
        )
        const purchaseButtonContainerStyles = purchaseButtonContainer?.properties?.find(
          (prop) => prop.property.type === "styles" && prop.prefix === "default"
        )
        const purchaseButtonContainerFlexDirection =
          // @ts-ignore
          purchaseButtonContainerStyles?.property?.styles?.find(
            (style) => style.property === "flex-direction"
          )?.value
        const isContainerColumnReverse = purchaseButtonContainerFlexDirection === "column-reverse"
        const paddingBottom = isContainerColumnReverse ? "14px" : "0px"
        return {
          ...baseSub,
          properties: [
            ...(restProps || []),
            {
              prefix: "default",
              property: {
                type: "styles",
                styles: [
                  // @ts-ignore
                  ...(stylesProps[0]?.property?.styles || []),
                  { property: "padding-bottom", value: paddingBottom },
                ],
              },
            },
          ],
        }
      }

      return baseSub
    } else if (sub.key.includes("Purchase Drawer > Timeline")) {
      return {
        ...sub,
        key: sub.key.replaceAll("Purchase Drawer > ", ""),
      }
    } else if (sub.key.includes("> Purchase Subtitle")) {
      const { productDescription } = pageSettings
      const hideProductDescription = productDescription === "hide"
      const [stylesProps, restProps] = _.partition(sub.properties, (prop) => {
        return prop.property.type === "styles" && prop.prefix === "default"
      })

      return {
        ...sub,
        key: sub.key.replace("Purchase Drawer > ", `Products ${productsId.toLowerCase()} > `),
        properties: [
          ...(restProps || []),
          {
            prefix: "default",
            property: {
              type: "styles",
              styles: [
                // @ts-ignore
                ...(stylesProps[0]?.property?.styles || []),
                { property: "margin-bottom", value: "0px" },
                ...(hideProductDescription ? [{ property: "display", value: "none" }] : []),
              ],
            },
          },
        ],
      }
    } else {
      return {
        ...sub,
        key: sub.key.replace("Purchase Drawer > ", `Products ${productsId.toLowerCase()} > `),
      }
    }
  })

  const [cardContentContainer, restDrawerSubs] = _.partition(
    newDrawerAndProductsSubs,
    (sub) => sub.key === `Div ${div2Id.toLowerCase()}`
  )

  /**
   * The `Purchase Drawer > Container` element can sometimes be picked up as
   * `Purcahse Drawer > Has Content` if it is given children. Because of this
   * we need to combine these two subs into one. We'll just merge all the properties
   * from both subs into one and then combine all the default styles into a single sub property.
   * It's not the best solution but hoping this will cover almost all cases, since it might be
   * somewhat rare to have styles in both and even less likely that they have conflicting styles.
   */
  if (cardContentContainer.length > 1) {
    const container1 = cardContentContainer[0]
    const [container1StyleProps, restContainer1Props] = _.partition(
      container1?.properties,
      (prop) => prop.property.type === "styles" && prop.prefix === "default"
    )

    const container2 = cardContentContainer[1]
    const [container2StyleProps, restContainer2Props] = _.partition(
      container2?.properties,
      (prop) => prop.property.type === "styles" && prop.prefix === "default"
    )

    const combinedContainer = {
      ...cardContentContainer[0],
      properties: [
        ...restContainer1Props,
        ...restContainer2Props,
        {
          prefix: "default",
          property: {
            type: "styles",
            styles: [
              // @ts-ignore
              ...(container1StyleProps[0]?.property?.styles || []),
              // @ts-ignore
              ...(container2StyleProps[0]?.property?.styles || []),
              { property: "padding-top", value: "calc(var(--sw-container-padding))" },
              { property: "padding-right", value: "calc(var(--sw-container-padding))" },
              { property: "padding-left", value: "calc(var(--sw-container-padding))" },
              { property: "padding-bottom", value: "0px" },
            ],
          },
        },
      ],
    }
    newDrawerAndProductsSubs = [...restDrawerSubs, combinedContainer] as Sub[]
  }

  const productsBaseSubstitution = {
    key: `Products ${productsId.toLowerCase()}`,
    value: "",
    skipInnerHTML: true,
    tagName: "div",
    subType: "var",
    properties: [
      {
        prefix: "default",
        property: {
          type: "styles",
          styles: [
            { property: "padding-right", value: "var(--sw-container-padding)" },
            { property: "padding-left", value: "var(--sw-container-padding)" },
          ],
        },
      },
    ],
  } as Sub

  return [...restSubs, productsBaseSubstitution, ...newDrawerAndProductsSubs] as Sub[]
}

const isSubVisibilityHidden = (sub: Sub): { isHidden: boolean; restProps: TSubProperty[] } => {
  const [visibilityProp, restProps] = _.partition(
    sub.properties,
    (prop) => prop?.property?.type === "hidden" && prop?.property?.hidden === true
  )
  return {
    isHidden: visibilityProp.length > 0,
    restProps: restProps,
  }
}

export const footerSubstitutionsMigrationScript = (
  substitutions: Sub[],
  pageSettings: TPageSettings
): Sub[] => {
  // The "restore-1" sub key is used for the middle element of the normal/default footer.
  // Make sure to handle that the same as the rest of the footer subs.
  const [footerSubs, restSubs] = _.partition(
    substitutions,
    (sub) => sub.key === "footer" || sub.key.startsWith("Footer") || sub.key === "restore-1"
  )

  if (pageSettings?.footer === "hide") return restSubs

  const footerAlignment = pageSettings?.footerAlignment === "center" ? "center" : "space-around"

  let migratedFooterSubs: Sub[] = []

  if (pageSettings?.footer === "normal") {
    const footerItemStyles = (isHidden = false) => {
      return [
        { property: "display", value: isHidden ? "none" : "inline-block" },
        { property: "font-size", value: "calc(var(--sw-font-size) * 0.75);" },
      ]
    }

    _.forEach(footerSubs, (sub) => {
      const { isHidden } = isSubVisibilityHidden(sub)
      const [stylesProps, restProps] = _.partition(
        sub.properties,
        (prop) => prop?.property?.type === "styles" && prop?.prefix === "default"
      )
      // @ts-ignore
      const originalStyles = stylesProps[0]?.property?.styles || []
      switch (sub.key) {
        case "footer": {
          migratedFooterSubs.push({
            ...sub,
            key: `Div ${footerIds[0]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [
                    ...mergeAndDedupeStyles(
                      [
                        { property: "display", value: isHidden ? "none" : "flex" },
                        { property: "flex-direction", value: "row" },
                        { property: "justify-content", value: footerAlignment },
                        { property: "align-items", value: "center" },
                        { property: "gap", value: "var(--sw-font-size)" },
                        {
                          property: "padding-top",
                          value: "calc(var(--sw-container-padding) * 0.6)",
                        },
                        {
                          property: "padding-bottom",
                          value: "calc(var(--sw-container-padding) * 0.6)",
                        },
                      ],
                      originalStyles
                    ),
                  ],
                },
              },
            ],
          })
          break
        }
        case "Footer First": {
          const { isHidden } = isSubVisibilityHidden(sub)
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[1]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        case "restore-1": {
          // This is the middle element of the normal footer
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[2]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        case "Footer Last": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[3]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        default: {
          break
        }
      }
    })
  } else if (pageSettings?.footer === "compact") {
    const footerItemStyles = (isHidden = false) => [
      { property: "display", value: isHidden ? "none" : "inline-block" },
      { property: "padding-top", value: "0" },
      { property: "padding-bottom", value: "0" },
      { property: "padding-left", value: "calc(var(--sw-font-size) * 0.125)" },
      { property: "padding-right", value: "calc(var(--sw-font-size) * 0.125)" },
      { property: "font-size", value: "calc(var(--sw-font-size) * 0.75);" },
    ]

    _.forEach(footerSubs, (sub) => {
      const { isHidden } = isSubVisibilityHidden(sub)
      const [stylesProps, restProps] = _.partition(
        sub.properties,
        (prop) => prop?.property?.type === "styles" && prop?.prefix === "default"
      )
      // @ts-ignore
      const originalStyles = stylesProps[0]?.property?.styles || []

      switch (sub.key) {
        case "footer": {
          migratedFooterSubs.push({
            ...sub,
            key: `Div ${footerIds[0]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [
                    ...mergeAndDedupeStyles(
                      [
                        { property: "display", value: isHidden ? "none" : "flex" },
                        { property: "flex-direction", value: "row" },
                        { property: "justify-content", value: "center" },
                        { property: "align-items", value: "center" },
                        {
                          property: "padding-top",
                          value: "calc(var(--sw-container-padding) * 0.6)",
                        },
                        {
                          property: "padding-bottom",
                          value: "calc(var(--sw-container-padding) * 0.6)",
                        },
                      ],
                      originalStyles
                    ),
                  ],
                },
              },
            ],
          })
          break
        }
        case "Footer Column 1": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[1]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        case "Footer Column 2": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[2]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        case "Footer Column 3": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[3]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        case "Footer Column 4": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[4]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        case "Footer Column 5": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[5]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        default: {
          break
        }
      }
    })
  } else if (pageSettings?.footer === "minimal") {
    const footerItemStyles = (isHidden = false) => [
      { property: "display", value: isHidden ? "none" : "inline-block" },
      { property: "font-size", value: "calc(var(--sw-font-size) * 0.75);" },
    ]

    _.forEach(footerSubs, (sub) => {
      const { isHidden } = isSubVisibilityHidden(sub)
      const [stylesProps, restProps] = _.partition(
        sub.properties,
        (prop) => prop?.property?.type === "styles" && prop?.prefix === "default"
      )
      // @ts-ignore
      const originalStyles = stylesProps[0]?.property?.styles || []

      switch (sub.key) {
        case "footer": {
          migratedFooterSubs.push({
            ...sub,
            key: `Div ${footerIds[0]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [
                    ...mergeAndDedupeStyles(
                      [
                        { property: "display", value: isHidden ? "none" : "flex" },
                        { property: "flex-direction", value: "row" },
                        { property: "justify-content", value: footerAlignment },
                        { property: "align-items", value: "center" },
                        { property: "gap", value: "calc(var(--sw-font-size) * 1.5)" },
                        {
                          property: "padding-top",
                          value: "calc(var(--sw-container-padding) * 0.6)",
                        },
                        {
                          property: "padding-bottom",
                          value: "calc(var(--sw-container-padding) * 0.6)",
                        },
                      ],
                      originalStyles
                    ),
                  ],
                },
              },
            ],
          })
          break
        }
        case "Footer Minimal First": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[1]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        case "Footer Minimal Last": {
          migratedFooterSubs.push({
            ...sub,
            key: `Text ${footerIds[2]?.toLowerCase()}`,
            properties: [
              ...restProps,
              {
                prefix: "default",
                property: {
                  type: "styles",
                  styles: [...mergeAndDedupeStyles(footerItemStyles(isHidden), originalStyles)],
                },
              },
            ],
          })
          break
        }
        default: {
          break
        }
      }
    })
  }

  const isHideProducts = pageSettings?.hideProducts === "true" ? true : false
  const isMoreThanOneProduct = ["two", "three"].includes(pageSettings?.productCount || "")
  const addDividerMarginTop = isHideProducts && isMoreThanOneProduct
  const isFooterDividerVisible = pageSettings?.footerDivider === "show"

  /**
   * If the paywall is set to hide products and there is more than one product and
   * the footer divider is visible, then we'll make it add extra margin to its top
   * to account for the space needed between the footer and the products section.
   */
  const footerDividerSub = {
    key: `Divider ${footerDividerId.toLowerCase()}`,
    value: "",
    skipInnerHTML: true,
    tagName: "div",
    subType: "var",
    properties: [
      {
        prefix: "default",
        property: {
          type: "styles",
          styles: [
            // addDividerMarginTop
            // ? { property: "margin-top", value: "calc(var(--sw-container-padding) * 0.9)" }
            // : {},
            { property: "margin-bottom", value: "calc(var(--sw-container-padding)/2)" },
          ],
        },
      },
    ],
  } as Sub

  /**
   * If the paywall is set to hide products and there is more than one product and
   * the footer divider is hidden, then we'll make the footer component add extra
   * margin to its top to account for the space needed between the footer and products section.
   */
  if (!isFooterDividerVisible && addDividerMarginTop) {
    migratedFooterSubs = _.map(migratedFooterSubs, (sub) => {
      if (sub.key === `Div ${footerIds[0]?.toLowerCase()}`) {
        return {
          ...sub,
          properties: _.map(sub.properties, (prop) => {
            if (prop.property.type === "styles" && prop.prefix === "default") {
              return {
                ...prop,
                property: {
                  ...prop.property,
                  styles: [
                    ...prop.property.styles,
                    { property: "margin-top", value: "calc(var(--sw-container-padding)/2)" },
                  ],
                },
              }
            }

            return prop
          }),
        }
      }

      return sub
    }) as Sub[]
  }

  return [...restSubs, ...migratedFooterSubs, footerDividerSub]
}

export const createFooterComponent = (pageSettings): TElement[] => {
  if (pageSettings?.footer === "hide") return []

  let footerComponents: TElement[] = []

  const showDivider = pageSettings?.footerDivider === "show"

  if (showDivider) {
    footerComponents.push(createComponent("divider", { defaultId: footerDividerId }))
  }

  if (pageSettings?.footer === "normal") {
    footerComponents.push(createDefaultFooter(footerIds))
  } else if (pageSettings?.footer === "compact") {
    footerComponents.push(createCompactFooter(footerIds))
  } else if (pageSettings?.footer === "minimal") {
    footerComponents.push(createMinimalFooter(footerIds))
  }

  return footerComponents
}

/**
 * This creates the div that wraps the card content. This wrapper should
 * only be respoonsible for adding extra padding around the card
 */
const createWrappedCardContent = (cardContent: TElement[]): TElement[] => {
  if (!cardContent?.length) return []

  return [
    {
      id: div1Id,
      ...defaultDivComponent,
      children: [
        {
          id: div2Id,
          ...defaultDivComponent,
          children: cardContent,
        },
      ],
    },
  ]
}

export const createDrawerAndProductsComponents = (
  pageSettings,
  cardContent: TElement[] = []
): TElement[] => {
  const footerPosition = pageSettings?.footerPosition || "fixed"
  const footer = createFooterComponent(pageSettings)

  const wrappedCardContent = createWrappedCardContent(cardContent)

  return [
    ...(footerPosition === "inline" ? footer : []),
    {
      id: drawerId,
      name: "drawer",
      settings: {
        cardBackgroundBlur: pageSettings.cardBackgroundBlur === "true" ? true : false,
        containerRadius: pageSettings.containerRadius || "25px",
      },
      children: [
        ...wrappedCardContent,
        {
          id: productsId,
          name: "products",
          settings: {
            productCount: pageSettings.productCount || "one",
            hideProducts: pageSettings.hideProducts || "auto",
            direction: pageSettings.direction || "vertical",
            selectedBorderWidth: pageSettings.selectedBorderWidth || "2px",
            selectedCheckBoxImage:
              pageSettings.selectedCheckBoxImage ||
              "https://static.superwallassets.com/uoQsDwmrnamiqkfkzRKvC",
            borderWidth: pageSettings.borderWidth || "1px",
            borderColor: pageSettings.borderColor || "rgb(70,70,70)",
            unselectedCheckBoxImage:
              pageSettings.unselectedCheckBoxImage ||
              "https://static.superwallassets.com/ElRGbHkiRQTvFQ3Gojbfu",
            checkMarkMargin: pageSettings.checkMarkMargin || "0 12px 0 4px",
            reverseDirection: pageSettings.reverseDirection === "true" ? true : false,
            showProductCheckMark: pageSettings.showProductCheckMark || "hide",
            primaryProductBadge: pageSettings.primaryProductBadge || "show",
            secondaryProductBadge: pageSettings.secondaryProductBadge || "hide",
            tertiaryProductBadge: pageSettings.tertiaryProductBadge || "hide",
          },
        },
        ...(footerPosition === "fixed" ? footer : []),
      ],
    },
  ]
}
